'use client';

import { useMemo, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import {
  bindOnceTextReveal,
  TEXT_REVEAL_FULL,
  TEXT_REVEAL_SUBTLE,
} from 'components/landing/v3/scrollBeat';
import v3 from 'components/landing/v3/v3.module.css';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { homeAssetSrc } from '../homeAssetSrc';
import { ProductSpotlight } from './ProductSpotlight';
import { MobileStickyCtaBar } from './MobileStickyCtaBar';
import type {
  FormFactorsSectionProps,
  ProductSpotlightConfig,
  SpotlightFeature,
} from './types';
import styles from './FormFactorsSection.module.css';

const PRO_BANNER_IMAGE = homeAssetSrc('/home/form-factors/ring-pro-poster.png');
const AIR_BANNER_IMAGE = homeAssetSrc('/home/form-factors/ring-air-poster.png');
const PRO_BANNER_VIDEO = getCompressedAssetUrl(
  homeAssetSrc('/web_v2/home-v4/ring-pro-air/ring-pro.mp4'),
);
const AIR_BANNER_VIDEO = getCompressedAssetUrl(
  homeAssetSrc('/web_v2/home-v4/ring-pro-air/ring-air.mp4'),
);
/* Exact first frame of each banner video, so the poster->video handover is a
   straight swap. `*_BANNER_IMAGE` is a marketing still of the same product and
   visibly changes picture when the video paints. */
const PRO_BANNER_VIDEO_POSTER = getCompressedAssetUrl(
  homeAssetSrc('/web_v2/home-v4/video-posters/ring-pro/first-frame.jpg'),
);
const AIR_BANNER_VIDEO_POSTER = getCompressedAssetUrl(
  homeAssetSrc('/web_v2/home-v4/video-posters/ring-air/first-frame.jpg'),
);

const PRO_FEATURE_ICONS = [
  homeAssetSrc('/home/form-factors/icon-pro-battery.svg'),
  homeAssetSrc('/home/form-factors/icon-pro-storage.svg'),
  homeAssetSrc('/home/form-factors/icon-pro-battery.svg'),
  homeAssetSrc('/home/form-factors/icon-pro-heart.svg'),
] as const;

const AIR_FEATURE_ICONS = [
  homeAssetSrc('/home/form-factors/icon-air-lighter.svg'),
  homeAssetSrc('/home/form-factors/icon-air-battery.svg'),
  homeAssetSrc('/home/form-factors/icon-air-subscription.svg'),
  homeAssetSrc('/home/form-factors/icon-air-comfort.svg'),
] as const;

function featureRows(
  titles: string[],
  bodies: string[],
  icons: readonly string[],
): SpotlightFeature[] {
  return titles.map((title, index) => ({
    icon: icons[index] ?? icons[0],
    title,
    body: bodies[index] ?? '',
  }));
}

export const FormFactorsSection = ({
  'aria-label': ariaLabel,
}: FormFactorsSectionProps) => {
  const t = useTranslations('homepage');
  const { region } = useRegionLocale();
  const upperCountry = (region || '').toUpperCase();
  const isUS = upperCountry === 'US' || upperCountry === 'PR';

  const rootRef = useRef<HTMLDivElement | null>(null);
  const airBlockRef = useRef<HTMLElement | null>(null);
  const gsapCtx = useGsapContext(rootRef);

  useIsomorphicLayoutEffect(() => {
    gsapCtx.add(() => {
      const root = rootRef.current;
      if (!root) return;

      const unbinds: Array<() => void> = [];

      root.querySelectorAll<HTMLElement>('[data-ff-block]').forEach((block) => {
        const title = block.querySelector<HTMLElement>('[data-ff-title]');
        const faint = block.querySelector<HTMLElement>('[data-ff-faint]');
        if (title) {
          unbinds.push(
            bindOnceTextReveal(title, { ...TEXT_REVEAL_FULL, trigger: title }),
          );
          if (faint) {
            unbinds.push(
              bindOnceTextReveal(faint, {
                ...TEXT_REVEAL_SUBTLE,
                delay: 0.08,
                trigger: title,
              }),
            );
          }
        }

        const name = block.querySelector<HTMLElement>('[data-ff-name]');
        if (name) {
          unbinds.push(
            bindOnceTextReveal(name, { ...TEXT_REVEAL_FULL, trigger: name }),
          );
        }

        block.querySelectorAll<HTMLElement>('[data-ff-card]').forEach((card) => {
          const copies = Array.from(
            card.querySelectorAll<HTMLElement>('[data-ff-card-copy]'),
          );
          if (!copies.length) return;
          unbinds.push(
            bindOnceTextReveal(copies, {
              ...TEXT_REVEAL_FULL,
              stagger: 0.06,
              trigger: card,
            }),
          );
        });
      });

      return () => {
        unbinds.forEach((unbind) => unbind());
      };
    });
  }, [gsapCtx]);

  const products = useMemo((): ProductSpotlightConfig[] => {
    const learnMore = t('homepage.twoFormFactors.learnMore');

    const pro: ProductSpotlightConfig = {
      id: 'ring-pro',
      theme: 'dark',
      name: t('homepage.twoFormFactors.pro.name'),
      image: PRO_BANNER_IMAGE,
      imageAlt: t('homepage.twoFormFactors.pro.imageAlt'),
      video: PRO_BANNER_VIDEO,
      videoPoster: PRO_BANNER_VIDEO_POSTER,
      primaryCta: {
        label: t('homepage.cta.buyNow'),
        href: '/ring-pro/buy',
        buttonType: 'two_form_factors_pro_buy_button',
        legacyButtonType: 'ring_pro_banner_buy_button',
      },
      secondaryCta: {
        label: learnMore,
        href: '/ring-pro',
        buttonType: 'two_form_factors_pro_learn_button',
        legacyButtonType: 'ring_pro_banner_learn_button',
      },
      panels: [
        { type: 'intro', body: t('homepage.twoFormFactors.pro.panels.intro') },
        {
          type: 'metric',
          variant: 'battery',
          eyebrow: t('homepage.twoFormFactors.pro.panels.metric.eyebrow'),
          value: t('homepage.twoFormFactors.pro.panels.metric.value'),
          unit: t('homepage.twoFormFactors.pro.panels.metric.unit'),
          caption: t('homepage.twoFormFactors.pro.panels.metric.caption'),
        },
        {
          type: 'features',
          eyebrow: t('homepage.twoFormFactors.pro.kicker'),
          items: featureRows(
            [
              t('homepage.twoFormFactors.pro.panels.features.0.title'),
              t('homepage.twoFormFactors.pro.panels.features.1.title'),
              t('homepage.twoFormFactors.pro.panels.features.2.title'),
              t('homepage.twoFormFactors.pro.panels.features.3.title'),
            ],
            [
              t('homepage.twoFormFactors.pro.panels.features.0.body'),
              t('homepage.twoFormFactors.pro.panels.features.1.body'),
              t('homepage.twoFormFactors.pro.panels.features.2.body'),
              t('homepage.twoFormFactors.pro.panels.features.3.body'),
            ],
            PRO_FEATURE_ICONS,
          ),
        },
      ],
    };

    if (isUS) return [pro];

    const air: ProductSpotlightConfig = {
      id: 'ring-air',
      theme: 'light',
      name: t('homepage.twoFormFactors.air.name'),
      image: AIR_BANNER_IMAGE,
      imageAlt: t('homepage.twoFormFactors.air.imageAlt'),
      video: AIR_BANNER_VIDEO,
      videoPoster: AIR_BANNER_VIDEO_POSTER,
      primaryCta: {
        label: t('homepage.cta.buyNow'),
        href: '/ring/buy',
        buttonType: 'two_form_factors_air_buy_button',
        legacyButtonType: 'ring_banner_buy_button',
      },
      secondaryCta: {
        label: learnMore,
        href: '/ring',
        buttonType: 'two_form_factors_air_learn_button',
        legacyButtonType: 'ring_banner_learn_button',
      },
      panels: [
        { type: 'intro', body: t('homepage.twoFormFactors.air.panels.intro') },
        {
          type: 'metric',
          variant: 'weight',
          eyebrow: t('homepage.twoFormFactors.air.panels.metric.eyebrow'),
          value: t('homepage.twoFormFactors.air.panels.metric.value'),
          caption: t('homepage.twoFormFactors.air.panels.metric.caption'),
        },
        {
          type: 'features',
          eyebrow: t('homepage.twoFormFactors.air.kicker'),
          items: featureRows(
            [
              t('homepage.twoFormFactors.air.panels.features.0.title'),
              t('homepage.twoFormFactors.air.panels.features.1.title'),
              t('homepage.twoFormFactors.air.panels.features.2.title'),
              t('homepage.twoFormFactors.air.panels.features.3.title'),
            ],
            [
              t('homepage.twoFormFactors.air.panels.features.0.body'),
              t('homepage.twoFormFactors.air.panels.features.1.body'),
              t('homepage.twoFormFactors.air.panels.features.2.body'),
              t('homepage.twoFormFactors.air.panels.features.3.body'),
            ],
            AIR_FEATURE_ICONS,
          ),
        },
      ],
    };

    return [pro, air];
  }, [t, isUS]);

  const [pro, air] = products;

  return (
    <div
      ref={rootRef}
      className={styles.root}
      role="group"
      aria-label={ariaLabel}
    >
      <section
        className={`${styles.block} ${styles.blockDark}`}
        data-ff-block
        data-overlap-layer="pro"
        aria-labelledby="form-factor-pro-title"
      >
        <div className={styles.inner}>
          <div className={styles.headline}>
            <h2
              id="form-factor-pro-title"
              className={`${v3.subTitle} ${styles.title}`}
            >
              <span data-ff-title>{pro.name}</span>
              <br />
              <span className={v3.titleFaintOnDark} data-ff-faint>
                {t('homepage.twoFormFactors.pro.tagline')}
              </span>
            </h2>
          </div>
          <ProductSpotlight product={pro} labelledBy="form-factor-pro-title" />
        </div>
      </section>

      {air ? (
        <section
          ref={airBlockRef}
          className={`${styles.block} ${styles.blockLight}`}
          data-ff-block
          data-overlap-layer="air"
          aria-labelledby="form-factor-air-title"
        >
          <div className={styles.inner}>
            <div className={styles.headline}>
              <h2
                id="form-factor-air-title"
                className={`${v3.subTitle} ${styles.title}`}
              >
                <span data-ff-title>{air.name}</span>
                <br />
                <span className={v3.titleFaint} data-ff-faint>
                  {t('homepage.twoFormFactors.air.tagline')}
                </span>
              </h2>
            </div>
            <ProductSpotlight
              product={air}
              labelledBy="form-factor-air-title"
            />
          </div>
        </section>
      ) : null}

      <div className={`${styles.stickySlot} chat-fab-clearance`}>
        <MobileStickyCtaBar
          pro={pro}
          air={air}
          airBlockRef={airBlockRef}
          sectionRef={rootRef}
        />
      </div>
    </div>
  );
};

export default FormFactorsSection;
